import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Confirmation, Visible } from 'coreOld/components';
import { Icon, TextLine, TextLines } from 'coreOld/elements';
import { ButtonType, Size } from 'coreOld/enums';
import AddCostNote from 'partsOld/components/AddCostNote';
import { PartNumberHelper } from 'partsOld/helpers';
import { ICompanyPart, ICostNote, ISelectedNote } from 'partsOld/types';

import {
  ButtonsWrapper,
  editIconStyle,
  NoteContent,
  NoteLabel,
  NoteWrapper,
  removeIconStyle,
} from './companyPartNotesStyle';

const PREVIEW_LENGTH = 50;

interface IProps {
  setSelectedNote: Dispatch<SetStateAction<ISelectedNote>>;
  setEditedNote: Dispatch<SetStateAction<ICostNote>>;
  selectedNote: ISelectedNote;
  isConfirmationOpen: boolean;
  editedNote: ICostNote;
  part: ICompanyPart;
  onConfirmRemove: () => Promise<void>;
  onIconClick: (isRemoving: boolean) => void;
  onCloseModal: () => void;
}

const getNoteContent = (note: string) => {
  if (!note) {
    return;
  }

  if (note.length <= PREVIEW_LENGTH) {
    return note;
  }

  return `${note.substring(0, PREVIEW_LENGTH)}...`;
};

const CompanyPartsNotesView = ({
  part,
  setSelectedNote,
  isConfirmationOpen,
  editedNote,
  selectedNote,
  setEditedNote,
  onConfirmRemove,
  onIconClick,
  onCloseModal,
}: IProps) => {
  const { t } = useTranslation();

  return (
    <>
      <TextLine text={PartNumberHelper.toLabel(part.part)} />

      <TextLines
        texts={[
          part.manufacturerName,
          part.companyManufacturerName && `[${part.companyManufacturerName}]`,
          PartNumberHelper.toLabel(part.vendorNumber),
        ]}
        isSubText
      />

      <Visible when={Boolean(part.costNote)}>
        <NoteWrapper>
          <NoteContent>
            <NoteLabel>{t('prefixCostNote')}</NoteLabel>

            <ButtonsWrapper>
              <Icon name={Icon.PENCIL} style={editIconStyle} onClick={() => onIconClick(false)} />

              <Icon name={Icon.CLEAR} style={removeIconStyle} onClick={() => onIconClick(true)} />
            </ButtonsWrapper>
          </NoteContent>

          <div
            dangerouslySetInnerHTML={{
              __html: getNoteContent(editedNote?.content || part.costNote),
            }}
          />
        </NoteWrapper>

        <Visible when={Boolean(selectedNote) && !isConfirmationOpen}>
          <AddCostNote
            setIsAddCostOpen={setSelectedNote as Dispatch<SetStateAction<boolean | ISelectedNote>>}
            isAddCostOpen={Boolean(selectedNote)}
            selectedNote={selectedNote}
            setEditedNote={setEditedNote}
          />
        </Visible>

        <Visible when={Boolean(isConfirmationOpen)}>
          <Confirmation
            isOpen={isConfirmationOpen}
            onConfirm={onConfirmRemove}
            onCancel={onCloseModal}
            confirmBtnLabel={t('buttonLabelRemove')}
            confirmBtnType={ButtonType.DANGER}
            size={Size.MD}
            title={t('labelRemoveNote')}
            message={t('messageRemoveNote')}
          />
        </Visible>
      </Visible>
    </>
  );
};

export default CompanyPartsNotesView;
